/**
 * welcome.ts — logic thuần của popup chào mừng sau khi ĐĂNG KÝ xong (WelcomeDialog, gắn trong AppLayout).
 *
 * Trang đăng ký đánh dấu "chờ chào" cho người dùng vừa tạo (OTP email / hoàn tất Google); popup hiện một lần ở trang đầu
 * tiên trong ứng dụng, bấm "Bắt đầu khám phá" → /onboarding. Dấu nằm trong localStorage theo id người dùng nên tải lại
 * trang chưa kịp đọc vẫn còn; đăng nhập thường KHÔNG đặt dấu → không hiện lại.
 */
import type { PricingPlan } from '@/hooks/use-billing'
import { planAiLimit, PLAN_AI_LABEL, type TokenRate } from '@/lib/ai-credits'
import { i18n } from '@/lib/i18n'
import { formatNumber } from '@/lib/utils'

/** Mã gói trong bảng giá dành cho tài khoản mới đăng ký. */
export const WELCOME_PLAN_ID = 'free'

const KEY = (userId: string) => `ecdp:welcome-pending:${userId}`

export function markWelcomePending(userId: string, store: Pick<Storage, 'setItem'> | null = safeLocal()) {
  try { store?.setItem(KEY(userId), '1') } catch { /* chế độ riêng tư */ }
}

export function isWelcomePending(userId: string | undefined, store: Pick<Storage, 'getItem'> | null = safeLocal()): boolean {
  if (!userId) return false
  try { return store?.getItem(KEY(userId)) === '1' } catch { return false }
}

export function clearWelcomePending(userId: string, store: Pick<Storage, 'removeItem'> | null = safeLocal()) {
  try { store?.removeItem(KEY(userId)) } catch { /* bỏ qua */ }
}

function safeLocal(): Storage | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}

export interface WelcomePlanLimit { key: string; value: string; label: string }

/**
 * Các hạn mức của gói theo bảng giá server (cùng cách ghi với trang Gói dịch vụ; null = không giới hạn).
 * KHÔNG có số mặc định trong mã: gói đổi hạn mức thì popup đổi theo; chưa có dữ liệu → danh sách rỗng.
 */
export function planLimits(plan: PricingPlan | undefined, rate?: TokenRate | null): WelcomePlanLimit[] {
  if (!plan) return []
  const num = (n: number | null | undefined) => (n == null ? i18n.t('sharedMisc:welcome.unlimited') : formatNumber(n))
  const ai = planAiLimit(plan, rate)
  return [
    { key: 'members', value: num(plan.included.members), label: i18n.t('sharedMisc:welcome.limits.members') },
    { key: 'official', value: num(plan.included.officialChannels), label: i18n.t('sharedMisc:welcome.limits.official') },
    { key: 'personal', value: num(plan.included.personalChannels), label: i18n.t('sharedMisc:welcome.limits.personal') },
    { key: 'contacts', value: num(plan.activeContactsPerMonth), label: i18n.t('sharedMisc:welcome.limits.contacts') },
    // Lượt AI tặng / tháng — chưa quy được (chưa tải ví) thì không hiện số đơn vị nội bộ.
    // Gói không tặng AI (Miễn phí) → không hiện dòng AI.
    ...(ai.value !== 0 && (ai.text || ai.value == null || ai.label !== PLAN_AI_LABEL)
      ? [{ key: 'ai', value: ai.text ? ai.text.replace(/ lượt AI$/, '') : num(ai.value), label: ai.label }]
      : []),
  ]
}
